import "./styles.css";
import {
  type DayUse,
  type EstimatorInput,
  type RegionKey,
  estimateSolar,
  formatEuro,
  formatNumber
} from "./solarCalculator";
import {
  type LeadFormData,
  qualifyLead,
  redactLeadForAnalytics,
  validateLead
} from "./leadQualification";
import { trackEvent } from "./analytics";

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("App root not found");
}

app.innerHTML = `
  <header class="hero">
    <nav class="nav" aria-label="Principal">
      <a class="brand" href="#top" aria-label="Solar Leads inicio">
        <span class="brand-mark">SL</span>
        <span>Solar Leads</span>
      </a>
      <div class="nav-links">
        <a href="#estimador">Calculadora</a>
        <a href="#presupuesto">Presupuesto</a>
        <a href="#supuestos">Supuestos</a>
      </div>
    </nav>

    <section class="hero-grid" id="top">
      <div class="hero-copy">
        <p class="eyebrow">Autoconsumo residencial · España</p>
        <h1>Calcula un rango solar útil antes de pedir presupuestos.</h1>
        <p class="lead">
          Estimamos potencia, número de paneles, inversión y ahorro anual con supuestos visibles.
          Después cualificamos la solicitud para enviarla a instaladores de tu zona.
        </p>
        <div class="hero-actions">
          <a class="button primary" href="#estimador">Calcular mi instalación</a>
          <a class="button ghost" href="#presupuesto">Quiero presupuesto</a>
        </div>
        <p class="trust-note">No prometemos ahorro garantizado ni subvenciones automáticas. La visita técnica y el contrato final mandan.</p>
      </div>

      <aside class="hero-card" aria-label="Modelo de monetización">
        <span class="pill">MVP monetizable</span>
        <h2>Lead cualificado, no formulario frío</h2>
        <p>Priorizamos propietarios, plazo de decisión, factura, tejado y zona. El instalador recibe contexto y expectativa honesta.</p>
        <dl class="mini-metrics">
          <div><dt>Fee objetivo</dt><dd>35–160 €</dd></div>
          <div><dt>Lead A</dt><dd>Decisor + plazo corto</dd></div>
          <div><dt>Privacidad</dt><dd>Datos mínimos</dd></div>
        </dl>
      </aside>
    </section>
  </header>

  <main>
    <section class="section estimator" id="estimador">
      <div class="section-heading">
        <p class="eyebrow">Calculadora solar</p>
        <h2>Introduce tus datos principales</h2>
        <p>Cuanto más real sea tu consumo y superficie, más útil será el rango. Puedes empezar con tu factura mensual.</p>
      </div>

      <div class="calculator-layout">
        <form id="estimator-form" class="panel form-grid" aria-label="Formulario estimador solar">
          <label>
            Provincia
            <input name="province" autocomplete="address-level2" value="Madrid" required />
          </label>

          <label>
            Zona solar aproximada
            <select name="region" required>
              <option value="center">Centro peninsular</option>
              <option value="north">Norte / cantábrico</option>
              <option value="mediterranean">Mediterráneo / valle del Ebro</option>
              <option value="south_canary">Sur / Canarias</option>
            </select>
          </label>

          <label>
            Consumo anual, si lo sabes (kWh)
            <input name="annualConsumptionKwh" type="number" min="0" step="50" placeholder="Ej. 4200" />
          </label>

          <label>
            Factura mensual aproximada (€)
            <input name="monthlyBillEuro" type="number" min="0" step="5" value="110" />
          </label>

          <label>
            Tejado/superficie útil (m²)
            <input name="roofAreaM2" type="number" min="10" step="1" value="38" required />
          </label>

          <label>
            Tipo de cubierta
            <select name="roofType" required>
              <option value="pitched">Inclinada</option>
              <option value="flat">Plana</option>
              <option value="unknown">No lo sé</option>
            </select>
          </label>

          <label>
            Uso durante el día
            <select name="dayUse" required>
              <option value="low">Bajo: casi nadie en casa</option>
              <option value="medium" selected>Medio</option>
              <option value="high">Alto: teletrabajo/actividad diurna</option>
            </select>
          </label>

          <label>
            Sombra estimada (%)
            <input name="shadingPercent" type="range" min="0" max="50" value="10" />
            <span id="shade-output" class="range-output">10%</span>
          </label>

          <label>
            Precio electricidad opcional (€/kWh)
            <input name="electricityPriceEuroKwh" type="number" min="0" step="0.01" placeholder="Ej. 0,22" />
          </label>

          <label>
            Compensación excedentes opcional (€/kWh)
            <input name="exportPriceEuroKwh" type="number" min="0" step="0.005" placeholder="Ej. 0,05" />
          </label>

          <label class="check-row">
            <input name="wantsBattery" type="checkbox" />
            <span>Quiero evaluar batería</span>
          </label>
        </form>

        <section class="panel results" aria-live="polite" aria-atomic="true">
          <div class="results-topline">
            <p class="eyebrow">Resultado orientativo</p>
            <span id="roof-warning" class="status-chip">Sin visita técnica</span>
          </div>
          <div id="estimate-cards" class="estimate-cards"></div>
          <div class="chart-card">
            <div class="chart-header">
              <h3>Qué explica el ahorro</h3>
              <p>Más autoconsumo directo suele pesar más que vender excedentes.</p>
            </div>
            <div class="bar-stack" aria-hidden="true">
              <span id="self-bar"></span>
              <span id="export-bar"></span>
            </div>
            <div class="legend">
              <span><i class="self"></i>Autoconsumo</span>
              <span><i class="export"></i>Excedentes</span>
            </div>
          </div>
          <details class="assumptions" open>
            <summary>Supuestos usados</summary>
            <ul id="assumptions-list"></ul>
          </details>
        </section>
      </div>
    </section>

    <section class="section quote" id="presupuesto">
      <div class="section-heading">
        <p class="eyebrow">Solicitud cualificada</p>
        <h2>Pide presupuesto con contexto para instaladores</h2>
        <p>El objetivo es evitar leads basura: confirmamos capacidad de decisión, plazo, zona y potencial económico.</p>
      </div>

      <form id="lead-form" class="panel lead-form">
        <div class="form-grid">
          <label>
            Nombre
            <input name="name" autocomplete="name" required />
          </label>
          <label>
            Email
            <input name="email" type="email" autocomplete="email" required />
          </label>
          <label>
            Teléfono
            <input name="phone" autocomplete="tel" placeholder="6XXXXXXXX" required />
          </label>
          <label>
            Código postal
            <input name="postalCode" inputmode="numeric" autocomplete="postal-code" maxlength="5" required />
          </label>
          <label>
            Provincia
            <input name="province" autocomplete="address-level2" value="Madrid" required />
          </label>
          <label>
            Capacidad de decisión
            <select name="ownership" required>
              <option value="owner">Soy propietario/a</option>
              <option value="authorized">Puedo autorizar la instalación</option>
              <option value="community">Comunidad de vecinos</option>
              <option value="tenant">Estoy de alquiler</option>
            </select>
          </label>
          <label>
            Plazo
            <select name="timeline" required>
              <option value="now">Quiero presupuesto ya</option>
              <option value="three_months">En menos de 3 meses</option>
              <option value="six_months">En 3–6 meses</option>
              <option value="researching">Estoy comparando</option>
            </select>
          </label>
          <label>
            Factura mensual (€)
            <input name="monthlyBillEuro" type="number" min="0" step="5" value="110" required />
          </label>
          <label>
            Superficie útil (m²)
            <input name="roofAreaM2" type="number" min="10" step="1" value="38" required />
          </label>
          <label class="check-row">
            <input name="wantsBattery" type="checkbox" />
            <span>También quiero valorar batería</span>
          </label>
        </div>
        <label>
          Comentarios para el instalador
          <textarea name="notes" rows="4" placeholder="Ej. orientación sur, chalet adosado, consumo alto por aerotermia..."></textarea>
        </label>
        <label class="check-row consent">
          <input name="consent" type="checkbox" required />
          <span>Acepto que Solar Leads use estos datos para contactar y derivar mi solicitud a instaladores colaboradores. No se vende como ahorro garantizado.</span>
        </label>
        <button class="button primary" type="submit">Solicitar presupuestos solares</button>
        <div id="lead-feedback" class="lead-feedback" role="status" aria-live="polite"></div>
      </form>
    </section>

    <section class="section evidence" id="supuestos">
      <div class="section-heading">
        <p class="eyebrow">Transparencia</p>
        <h2>Qué valida este MVP</h2>
      </div>
      <div class="evidence-grid">
        <article class="panel">
          <h3>Demanda e intención</h3>
          <p>SEO transaccional: precio instalación, calcular placas, ahorro, amortización y presupuesto por provincia.</p>
        </article>
        <article class="panel">
          <h3>Unidad económica</h3>
          <p>Hipótesis inicial: leads A vendidos por 70–160 €, B por 35–80 €. Se ajusta con cierre real por instalador.</p>
        </article>
        <article class="panel">
          <h3>Compliance</h3>
          <p>Rangos, supuestos visibles y advertencias. Las ayudas públicas se mencionan solo como comprobación pendiente por comunidad, nunca como promesa.</p>
        </article>
      </div>
    </section>
  </main>

  <footer class="footer">
    <p>Solar Leads · MVP experimental · Estimaciones no vinculantes.</p>
    <a href="#top">Volver arriba</a>
  </footer>
`;

const estimatorForm = document.querySelector<HTMLFormElement>("#estimator-form");
const leadForm = document.querySelector<HTMLFormElement>("#lead-form");
const shadeOutput = document.querySelector<HTMLSpanElement>("#shade-output");
const estimateCards = document.querySelector<HTMLDivElement>("#estimate-cards");
const assumptionsList = document.querySelector<HTMLUListElement>("#assumptions-list");
const roofWarning = document.querySelector<HTMLSpanElement>("#roof-warning");
const selfBar = document.querySelector<HTMLSpanElement>("#self-bar");
const exportBar = document.querySelector<HTMLSpanElement>("#export-bar");
const leadFeedback = document.querySelector<HTMLDivElement>("#lead-feedback");

function numberValue(formData: FormData, key: string): number | undefined {
  const value = String(formData.get(key) ?? "").replace(",", ".").trim();
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function collectEstimatorInput(): EstimatorInput {
  if (!estimatorForm) throw new Error("Estimator form missing");
  const formData = new FormData(estimatorForm);
  return {
    province: String(formData.get("province") ?? "Madrid"),
    region: String(formData.get("region") ?? "center") as RegionKey,
    annualConsumptionKwh: numberValue(formData, "annualConsumptionKwh"),
    monthlyBillEuro: numberValue(formData, "monthlyBillEuro"),
    roofAreaM2: numberValue(formData, "roofAreaM2") ?? 38,
    roofType: String(formData.get("roofType") ?? "pitched") as EstimatorInput["roofType"],
    dayUse: String(formData.get("dayUse") ?? "medium") as DayUse,
    shadingPercent: numberValue(formData, "shadingPercent") ?? 0,
    wantsBattery: formData.get("wantsBattery") === "on",
    electricityPriceEuroKwh: numberValue(formData, "electricityPriceEuroKwh"),
    exportPriceEuroKwh: numberValue(formData, "exportPriceEuroKwh")
  };
}

function renderEstimate(): void {
  if (!estimateCards || !assumptionsList || !roofWarning || !selfBar || !exportBar) return;

  const input = collectEstimatorInput();
  const estimate = estimateSolar(input);

  shadeOutput!.textContent = `${input.shadingPercent}%`;
  roofWarning.textContent = estimate.roofLimited ? "Tejado limitante" : "Rango no vinculante";
  roofWarning.classList.toggle("warning", estimate.roofLimited);

  const metrics = [
    {
      label: "Potencia recomendada",
      value: `${formatNumber(estimate.recommendedKwp.low)}–${formatNumber(estimate.recommendedKwp.high)} kWp`,
      detail: `${estimate.panels.low}–${estimate.panels.high} paneles aprox.`
    },
    {
      label: "Producción anual",
      value: `${formatNumber(estimate.annualProductionKwh.low)}–${formatNumber(estimate.annualProductionKwh.high)} kWh`,
      detail: `Consumo estimado: ${formatNumber(estimate.annualConsumptionKwh.low)}–${formatNumber(estimate.annualConsumptionKwh.high)} kWh/año`
    },
    {
      label: "Inversión estimada",
      value: `${formatEuro(estimate.investmentEuro.low)}–${formatEuro(estimate.investmentEuro.high)}`,
      detail: input.wantsBattery ? "Incluye rango prudente para batería." : "Sin batería ni obra singular."
    },
    {
      label: "Ahorro anual orientativo",
      value: `${formatEuro(estimate.annualSavingsEuro.low)}–${formatEuro(estimate.annualSavingsEuro.high)}`,
      detail: `Retorno simple: ${formatNumber(estimate.paybackYears.low)}–${formatNumber(estimate.paybackYears.high)} años`
    }
  ];

  estimateCards.innerHTML = metrics
    .map(
      (metric) => `
        <article class="metric-card">
          <span>${metric.label}</span>
          <strong>${metric.value}</strong>
          <p>${metric.detail}</p>
        </article>
      `
    )
    .join("");

  assumptionsList.innerHTML = [...estimate.assumptions, ...estimate.warnings]
    .map((item) => `<li>${item}</li>`)
    .join("");

  const self = estimate.selfConsumptionRate.low;
  const exported = Math.max(8, 100 - estimate.selfConsumptionRate.high);
  selfBar.style.width = `${Math.min(88, self)}%`;
  exportBar.style.width = `${Math.min(45, exported)}%`;

  trackEvent("estimate_updated", {
    province: input.province,
    region: input.region,
    recommendedKwpLow: estimate.recommendedKwp.low,
    recommendedKwpHigh: estimate.recommendedKwp.high,
    estimatedSavingsLow: estimate.annualSavingsEuro.low,
    estimatedSavingsHigh: estimate.annualSavingsEuro.high,
    roofLimited: estimate.roofLimited
  });
}

function collectLeadData(): LeadFormData {
  if (!leadForm) throw new Error("Lead form missing");
  const formData = new FormData(leadForm);
  return {
    name: String(formData.get("name") ?? ""),
    email: String(formData.get("email") ?? ""),
    phone: String(formData.get("phone") ?? ""),
    postalCode: String(formData.get("postalCode") ?? ""),
    province: String(formData.get("province") ?? ""),
    ownership: String(formData.get("ownership") ?? "owner") as LeadFormData["ownership"],
    timeline: String(formData.get("timeline") ?? "researching") as LeadFormData["timeline"],
    monthlyBillEuro: numberValue(formData, "monthlyBillEuro") ?? 0,
    roofAreaM2: numberValue(formData, "roofAreaM2") ?? 0,
    wantsBattery: formData.get("wantsBattery") === "on",
    notes: String(formData.get("notes") ?? ""),
    consent: formData.get("consent") === "on"
  };
}

async function submitLead(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (!leadFeedback) return;

  const lead = collectLeadData();
  const validationErrors = validateLead(lead);
  const qualification = qualifyLead(lead);

  trackEvent("lead_submit_attempt", {
    ...redactLeadForAnalytics(lead),
    score: qualification.score,
    tier: qualification.tier
  });

  if (validationErrors.length > 0) {
    leadFeedback.className = "lead-feedback error";
    leadFeedback.innerHTML = `<strong>Revisa estos puntos:</strong><ul>${validationErrors
      .map((error) => `<li>${error}</li>`)
      .join("")}</ul>`;
    trackEvent("lead_validation_failed", {
      errorCount: validationErrors.length,
      tier: qualification.tier
    });
    return;
  }

  const payload = {
    lead,
    qualification,
    estimate: collectEstimatorInput(),
    source: "solar-leads-mvp",
    createdAt: new Date().toISOString()
  };

  try {
    const response = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Lead API returned ${response.status}`);
    }

    const result = (await response.json()) as { id?: string };
    leadFeedback.className = "lead-feedback success";
    leadFeedback.innerHTML = `
      <strong>Solicitud cualificada (${qualification.tier}, ${qualification.score}/100).</strong>
      <p>Referencia: ${result.id ?? "pendiente"}. Próximo paso: ${qualification.nextStep}</p>
      <p>Valor lead estimado interno: ${formatEuro(qualification.estimatedLeadValueEuro.low)}–${formatEuro(qualification.estimatedLeadValueEuro.high)}.</p>
    `;
    leadForm?.reset();
    trackEvent("qualified_lead_created", {
      ...redactLeadForAnalytics(lead),
      score: qualification.score,
      tier: qualification.tier
    });
  } catch {
    localStorage.setItem("solar-leads:lastLead", JSON.stringify(payload));
    leadFeedback.className = "lead-feedback success";
    leadFeedback.innerHTML = `
      <strong>Solicitud preparada (${qualification.tier}, ${qualification.score}/100).</strong>
      <p>Se ha guardado localmente porque el endpoint no está disponible en este entorno. Próximo paso: ${qualification.nextStep}</p>
    `;
    trackEvent("qualified_lead_saved_locally", {
      tier: qualification.tier,
      score: qualification.score
    });
  }
}

estimatorForm?.addEventListener("input", renderEstimate);
leadForm?.addEventListener("submit", (event) => void submitLead(event));

renderEstimate();
trackEvent("page_view", { page: "home" });
